import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, BehaviorSubject } from 'rxjs';
import { GlobalComponent } from 'src/app/global-component';
import { environment } from 'src/environments/environment';

export interface SendMessageRequest {
  id?: number;
  senderId: number;
  receiverId: number;
  messageText: string;
  mediaUrl?: string;
  file?: string;
  contentType?: string;
  timestamp?: string;
  isRead?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AppSignalRService {
  private hubConnection!: signalR.HubConnection;
  private connectionStatus$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initializeConnection();
  }

  private initializeConnection(): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.mainBaseUrl + 'notificationHub', {
        accessTokenFactory: () => localStorage.getItem('token') || '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000]) // Retry connection with exponential backoff
      .build();

    // Handle connection state changes
    this.hubConnection.onreconnecting(() => {
      console.log('SignalR: Attempting to reconnect...');
      this.connectionStatus$.next(false);
    });

    this.hubConnection.onreconnected(() => {
      console.log('SignalR: Reconnected successfully');
      this.connectionStatus$.next(true);
    });

    this.hubConnection.onclose(() => {
      console.log('SignalR: Connection closed');
      this.connectionStatus$.next(false);
    });
  }

  //Starts the SignalR connection
  startConnection(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
        observer.next();
        observer.complete();
        return;
      }

      this.hubConnection
        .start()
        .then(() => {
          console.log('Connection established with SignalR hub');
          this.connectionStatus$.next(true);
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          console.error('Error connecting to SignalR hub:', error);
          this.connectionStatus$.next(false);
          observer.error(error);
        });
    });
  }

  //Ends the SignalR connection if it exists
  stopConnection(): Observable<void> {
    return new Observable<void>((observer) => {
      if (
        this.hubConnection &&
        this.hubConnection.state === signalR.HubConnectionState.Connected
      ) {
        this.hubConnection
          .stop()
          .then(() => {
            console.log('Connection stopped successfully');
            this.connectionStatus$.next(false);
            observer.next();
            observer.complete();
          })
          .catch((error) => {
            console.error('Error stopping the SignalR hub connection:', error);
            observer.error(error);
          });
      } else {
        console.warn('No active connection to stop');
        observer.complete();
      }
    });
  }

  // Listens for incoming messages from the SignalR hub
  receiveMessage(): Observable<SendMessageRequest> {
    return new Observable<SendMessageRequest>((observer) => {
      this.hubConnection.on('ReceiveMessage', (message: SendMessageRequest) => {
        console.log('Received message via SignalR:', message);
        observer.next(message);
      });
    });
  }

  // Sends a message to the SignalR hub
  sendMessage(message: SendMessageRequest): void {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('SendMessage', message).catch((error) => {
        console.error('Error sending message via SignalR:', error);
      });
    } else {
      console.warn('Cannot send message, no active SignalR connection');
    }
  }

  //Checks the current connection status
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  // Check if connection is currently active
  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}
